"use client";

import { useState, useEffect, useLayoutEffect, useRef } from "react";

const TEAL = "#4FCDC4";
const TEXT = "#EDEDED";
const TEXT_MUTED = "#a0a0a0";
const CARD_BORDER = "rgba(255,255,255,0.04)";

const APP = "https://app.ai-advisors.ai";
const utm = (medium, campaign, content) =>
  `${APP}?utm_source=website&utm_medium=${medium}&utm_campaign=${campaign}&utm_content=${content}`;

function useMedia(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

const PLATFORM_ICONS = {
  "AI Analytics": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  "Answer Engine Insights": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M13 8H7" /><path d="M17 12H7" />
    </svg>
  ),
  "Answer Engine Optimization": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  ),
  "AI Ads": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 13v-2z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  ),
  "AI Automation": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" />
    </svg>
  ),
};

const PLATFORM_LINKS = [
  { label: "AI Analytics", desc: "Track AI bot visits and referral traffic", href: "/platform/ai-analytics" },
  { label: "Answer Engine Insights", desc: "Monitor your brand across AI platforms", href: "/platform/answer-engine-insights" },
  { label: "Answer Engine Optimization", desc: "Audit and improve your AEO score", href: "/platform/aeo" },
  { label: "AI Ads", desc: "Launch and measure ChatGPT Ads", href: "/platform/ai-ads" },
  { label: "AI Automation", desc: "Automated audits, fixes, & monitoring", href: "/platform/ai-automation" },
];

const TOOL_ICONS = {
  // Lucide "gauge": a readiness-score dial for the 29-check audit.
  "AEO Audit": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 14 4-4" /><path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  ),
  "AI Visibility Checker": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ),
  "AI Bot Access Checker": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8V4H8" /><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" />
    </svg>
  ),
  "llms.txt Generator": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  "ChatGPT Ads Mockup": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 13v-2z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  ),
  // Lucide "circle-check": the pixel setup verified.
  "ChatGPT Ads Pixel Checker": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
    </svg>
  ),
  "ChatGPT Ads Converter": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m16 3 4 4-4 4" /><path d="M20 7H4" /><path d="m8 21-4-4 4-4" /><path d="M4 17h16" />
    </svg>
  ),
};

const TOOL_LINKS = [
  { label: "AEO Audit", desc: "29-check AI search readiness score", href: "/tools/aeo-audit" },
  { label: "AI Visibility Checker", desc: "See what AI platforms say about you", href: "/tools/ai-visibility-checker" },
  { label: "AI Bot Access Checker", desc: "Check if AI bots can reach your site", href: "/tools/ai-bot-checker" },
  { label: "llms.txt Generator", desc: "Create your AI visibility file", href: "/tools/llms-txt-generator" },
  { label: "ChatGPT Ads Mockup", desc: "Preview your ad in a real conversation", href: "/tools/chatgpt-ads-mockup" },
  { label: "ChatGPT Ads Pixel Checker", desc: "Test your OpenAI pixel setup", href: "/tools/chatgpt-ads-pixel-checker" },
  { label: "ChatGPT Ads Converter", desc: "Convert Google Ads to ChatGPT Ads", href: "/tools/google-ads-to-chatgpt-ads-converter" },
];

const RESOURCE_ICONS = {
  "Blog": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" /><path d="M18 14h-8" /><path d="M15 18h-5" /><path d="M10 6h8v4h-8V6Z" />
    </svg>
  ),
  "Glossary": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </svg>
  ),
  "AI Marketing Framework": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" /><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" /><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </svg>
  ),
  "Playbook": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" /><path d="M15 5.764v15" /><path d="M9 3.236v15" />
    </svg>
  ),
  "AI Bot Blocking Index": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8V4H8" /><rect width="16" height="12" x="4" y="8" rx="2" /><path d="M2 14h2" /><path d="M20 14h2" /><path d="M15 13v2" /><path d="M9 13v2" />
    </svg>
  ),
  "Google Ads to ChatGPT Ads Study": (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
    </svg>
  ),
};

const RESOURCE_LINKS = [
  { label: "Blog", desc: "Guides on AEO, ChatGPT Ads & AI search", href: "/blog" },
  { label: "Glossary", desc: "AI marketing terms, defined", href: "/glossary" },
  { label: "AI Marketing Framework", desc: "The 5 A's of AI Marketing", href: "/ai-marketing-framework" },
  { label: "Playbook", desc: "The 5 A's implementation guide + 90-day plan", href: "/playbook" },
  { label: "AI Bot Blocking Index", desc: "Original data: how 390 B2B SaaS sites treat AI crawlers", href: "/research/ai-bot-blocking-index" },
  { label: "Google Ads to ChatGPT Ads Study", desc: "Original data: 200 B2B campaigns converted", href: "/research/google-ads-to-chatgpt-ads-migration-study" },
];

function Chevron({ open }) {
  return (
    <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none", marginLeft: 1 }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Two-column desktop dropdown, used by Free Tools and Resources (Kevin's
// request, 2026-10-14). Fills top to bottom, left column first, so 7 items
// read 4 + 3 and 6 read 3 + 3; a new item lands on the left automatically.
// Both columns take the widest item's width. Doubling the width can push the
// panel past the window edge on smaller laptops, so it measures itself before
// paint and slides sideways to stay 16px inside the viewport. It renders only
// after a hover, never on the server, so useLayoutEffect is safe here.
function TwoColumnMenu({ count, style, children }) {
  const ref = useRef(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const edge = 16;
    let dx = 0;
    if (r.right > window.innerWidth - edge) dx = window.innerWidth - edge - r.right;
    if (r.left + dx < edge) dx = edge - r.left;
    setShift(dx);
  }, []);
  return (
    <div ref={ref} role="menu" style={{
      ...style,
      transform: `translateX(calc(-50% + ${shift}px))`,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: `repeat(${Math.ceil(count / 2)}, auto)`,
      gridAutoFlow: "column",
      columnGap: 24,
    }}>
      {children}
    </div>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobilePlatformOpen, setMobilePlatformOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const isMobile = useMedia("(max-width: 768px)");
  const platformTimer = useRef(null);
  const toolsTimer = useRef(null);
  const resourcesTimer = useRef(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const handle = (e) => {
      if (!e.target.closest("[data-dropdown='platform']")) setPlatformOpen(false);
      if (!e.target.closest("[data-dropdown='tools']")) setToolsOpen(false);
      if (!e.target.closest("[data-dropdown='resources']")) setResourcesOpen(false);
    };
    document.addEventListener("click", handle);
    return () => document.removeEventListener("click", handle);
  }, []);

  // Reset mobile accordion sections to collapsed whenever the mobile menu closes.
  // Each menu re-open should start from a clean top-level state (Profound pattern).
  useEffect(() => {
    if (!menuOpen) {
      setMobilePlatformOpen(false);
      setMobileToolsOpen(false);
      setMobileResourcesOpen(false);
    }
  }, [menuOpen]);

  const scrollYRef = useRef(0);
  const lockBody = () => {
    scrollYRef.current = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";
  };
  const unlockBody = () => {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.overflow = "";
    window.scrollTo(0, scrollYRef.current);
  };
  const toggleMenu = () => {
    if (menuOpen) { unlockBody(); setMenuOpen(false); }
    else { lockBody(); setMenuOpen(true); }
  };
  const closeMenu = () => {
    if (menuOpen) { unlockBody(); setMenuOpen(false); }
  };

  const dropdownBase = {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(12,12,14,0.98)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: 6,
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
    minWidth: 280, whiteSpace: "nowrap", width: "fit-content",
  };

  const navLinkStyle = {
    color: TEXT_MUTED, fontSize: 14, fontWeight: 500, fontFamily: "var(--body)",
    transition: "color 0.2s", textDecoration: "none",
  };

  const dropdownBtnStyle = (isOpen) => ({
    background: "none", border: "none", cursor: "pointer",
    color: isOpen ? TEXT : TEXT_MUTED,
    fontSize: 14, fontWeight: 500, fontFamily: "var(--body)",
    display: "flex", alignItems: "center", gap: 4, padding: 0,
    transition: "color 0.2s",
  });

  const dropdownItemStyle = {
    display: "block", padding: "10px 14px", borderRadius: 8,
    transition: "background 0.15s",
    textDecoration: "none",
  };

  return (
    <nav aria-label="Main navigation" suppressHydrationWarning style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, overflow: "visible",
      background: scrolled || menuOpen ? "rgba(9,9,11,0.8)" : "transparent",
      backdropFilter: scrolled || menuOpen ? "blur(16px)" : "none",
      WebkitBackdropFilter: scrolled || menuOpen ? "blur(16px)" : "none",
      borderBottom: `1px solid ${scrolled ? CARD_BORDER : "transparent"}`,
      transition: "background 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease",
    }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        padding: isMobile ? "0 20px" : "0 32px",
        height: 64,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        {/* Logo */}
        <a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none", flexShrink: 0 }}>
          <span style={{ color: TEAL, fontSize: isMobile ? 32 : 16, lineHeight: 1, transform: "translateY(-1px)" }}>&#10022;</span>
          <span style={{ fontFamily: "var(--brand)", fontWeight: 700, fontSize: isMobile ? 22 : 17, color: TEXT, letterSpacing: "-0.02em" }}>AI-Advisors</span>
        </a>

        {/* Center links (desktop) */}
        {!isMobile && (
          <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
            <div data-dropdown="platform" style={{ position: "relative" }}
              onMouseEnter={() => { clearTimeout(platformTimer.current); setPlatformOpen(true); setToolsOpen(false); setResourcesOpen(false); }}
              onMouseLeave={() => { platformTimer.current = setTimeout(() => setPlatformOpen(false), 180); }}
            >
              <button suppressHydrationWarning
                aria-expanded={platformOpen}
                aria-haspopup="true"
                style={dropdownBtnStyle(platformOpen)}
              >Platform <Chevron open={platformOpen} /></button>
              {platformOpen && (
                <div role="menu" style={dropdownBase}>
                  {PLATFORM_LINKS.map((l) => (
                    <a key={l.href} href={l.href} role="menuitem" onClick={() => setPlatformOpen(false)}
                      style={{ ...dropdownItemStyle, display: "flex", alignItems: "center", gap: 12 }}
                      onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: TEAL,
                      }}>
                        {PLATFORM_ICONS[l.label]}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div data-dropdown="tools" style={{ position: "relative" }}
              onMouseEnter={() => { clearTimeout(toolsTimer.current); setToolsOpen(true); setPlatformOpen(false); setResourcesOpen(false); }}
              onMouseLeave={() => { toolsTimer.current = setTimeout(() => setToolsOpen(false), 180); }}
            >
              <button suppressHydrationWarning
                aria-expanded={toolsOpen}
                aria-haspopup="true"
                style={dropdownBtnStyle(toolsOpen)}
              >Free Tools <Chevron open={toolsOpen} /></button>
              {toolsOpen && (
                <TwoColumnMenu count={TOOL_LINKS.length} style={dropdownBase}>
                  {TOOL_LINKS.map((l) => (
                    <a key={l.href} href={l.href} role="menuitem" onClick={() => setToolsOpen(false)}
                      style={{ ...dropdownItemStyle, display: "flex", alignItems: "center", gap: 12 }}
                      onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: TEAL,
                      }}>
                        {TOOL_ICONS[l.label]}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                      </div>
                    </a>
                  ))}
                </TwoColumnMenu>
              )}
            </div>

            <div data-dropdown="resources" style={{ position: "relative" }}
              onMouseEnter={() => { clearTimeout(resourcesTimer.current); setResourcesOpen(true); setPlatformOpen(false); setToolsOpen(false); }}
              onMouseLeave={() => { resourcesTimer.current = setTimeout(() => setResourcesOpen(false), 180); }}
            >
              <button suppressHydrationWarning
                aria-expanded={resourcesOpen}
                aria-haspopup="true"
                style={dropdownBtnStyle(resourcesOpen)}
              >Resources <Chevron open={resourcesOpen} /></button>
              {resourcesOpen && (
                <TwoColumnMenu count={RESOURCE_LINKS.length} style={dropdownBase}>
                  {RESOURCE_LINKS.map((l) => (
                    <a key={l.href} href={l.href} role="menuitem" onClick={() => setResourcesOpen(false)}
                      style={{ ...dropdownItemStyle, display: "flex", alignItems: "center", gap: 12 }}
                      onMouseOver={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                        background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: TEAL,
                      }}>
                        {RESOURCE_ICONS[l.label]}
                      </div>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                      </div>
                    </a>
                  ))}
                </TwoColumnMenu>
              )}
            </div>

            <a href="/integrations" style={navLinkStyle}
              onMouseOver={(e) => e.target.style.color = TEXT}
              onMouseOut={(e) => e.target.style.color = TEXT_MUTED}
            >Integrations</a>
            <a href="/pricing" style={navLinkStyle}
              onMouseOver={(e) => e.target.style.color = TEXT}
              onMouseOut={(e) => e.target.style.color = TEXT_MUTED}
            >Pricing</a>
            <a href="/about" style={navLinkStyle}
              onMouseOver={(e) => e.target.style.color = TEXT}
              onMouseOut={(e) => e.target.style.color = TEXT_MUTED}
            >About</a>
          </div>
        )}

        {/* Right CTAs (desktop) */}
        {!isMobile && (
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
            <a href={utm("nav", "signin", "nav_signin")} style={{
              ...navLinkStyle,
            }}
              onMouseOver={(e) => e.target.style.color = TEXT}
              onMouseOut={(e) => e.target.style.color = TEXT_MUTED}
            >Sign In</a>
            <a href={utm("nav", "signup", "nav_get_started")} style={{
              padding: "8px 20px", borderRadius: 10, background: TEAL, color: "#000",
              fontSize: 13, fontWeight: 600, fontFamily: "var(--body)",
              textDecoration: "none", transition: "opacity 0.2s",
            }}
              onMouseOver={(e) => e.currentTarget.style.opacity = "0.9"}
              onMouseOut={(e) => e.currentTarget.style.opacity = "1"}
            >Start Free Trial</a>
          </div>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button suppressHydrationWarning
            onClick={toggleMenu}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 14, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 44, minHeight: 44, overflow: "visible", position: "relative" }}
          >
            <span style={{ width: 20, height: 2, background: TEXT, borderRadius: 1, transition: "transform 0.3s, opacity 0.3s", position: "absolute", transform: menuOpen ? "rotate(45deg)" : "translateY(-4px)", transformOrigin: "center" }} />
            <span style={{ width: 20, height: 2, background: TEXT, borderRadius: 1, transition: "transform 0.3s, opacity 0.3s", opacity: menuOpen ? 0 : 1, position: "absolute", transformOrigin: "center" }} />
            <span style={{ width: 20, height: 2, background: TEXT, borderRadius: 1, transition: "transform 0.3s, opacity 0.3s", position: "absolute", transform: menuOpen ? "rotate(-45deg)" : "translateY(4px)", transformOrigin: "center" }} />
          </button>
        )}
      </div>

      {/* Mobile menu */}
      {isMobile && (
        <div style={{
          height: menuOpen ? "calc(100dvh - 64px)" : 0,
          overflow: "hidden",
          transition: "height 0.4s ease",
          borderTop: menuOpen ? `1px solid ${CARD_BORDER}` : "none",
          display: "flex",
          flexDirection: "column",
        }}>
          {/* Scrollable items area (Profound pattern: items scroll, buttons stay pinned). */}
          <div className="blog-scroll-wrap" style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            padding: "4px 20px 16px",
          }}>
          {/* Platform accordion */}
          <button suppressHydrationWarning
            onClick={() => setMobilePlatformOpen(!mobilePlatformOpen)}
            aria-expanded={mobilePlatformOpen}
            aria-controls="mobile-platform-section"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 0",
              background: "transparent",
              border: "none",
              color: TEXT,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: "var(--body)",
              cursor: "pointer",
              borderBottom: "1px solid rgba(255,255,255,0.03)",
            }}
          >
            Platform
            <span style={{ display: "inline-flex", transform: mobilePlatformOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease", color: TEXT_MUTED, fontSize: 12 }}>&#9660;</span>
          </button>
          <div id="mobile-platform-section" style={{
            maxHeight: mobilePlatformOpen ? 600 : 0,
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}>
            <div style={{ padding: "8px 0 12px" }}>
              {PLATFORM_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={closeMenu}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", textDecoration: "none" }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: TEAL,
                  }}>
                    {PLATFORM_ICONS[l.label]}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Free Tools accordion */}
          <button suppressHydrationWarning
            onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
            aria-expanded={mobileToolsOpen}
            aria-controls="mobile-tools-section"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 0",
              background: "transparent",
              border: "none",
              color: TEXT,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: "var(--body)",
              cursor: "pointer",
              borderBottom: "1px solid rgba(255,255,255,0.03)",
            }}
          >
            Free Tools
            <span style={{ display: "inline-flex", transform: mobileToolsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease", color: TEXT_MUTED, fontSize: 12 }}>&#9660;</span>
          </button>
          <div id="mobile-tools-section" style={{
            maxHeight: mobileToolsOpen ? 600 : 0,
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}>
            <div style={{ padding: "8px 0 12px" }}>
              {TOOL_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={closeMenu}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", textDecoration: "none" }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: TEAL,
                  }}>
                    {TOOL_ICONS[l.label]}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          {/* Resources accordion */}
          <button suppressHydrationWarning
            onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
            aria-expanded={mobileResourcesOpen}
            aria-controls="mobile-resources-section"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 0",
              background: "transparent",
              border: "none",
              color: TEXT,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: "var(--body)",
              cursor: "pointer",
              borderBottom: "1px solid rgba(255,255,255,0.03)",
            }}
          >
            Resources
            <span style={{ display: "inline-flex", transform: mobileResourcesOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s ease", color: TEXT_MUTED, fontSize: 12 }}>&#9660;</span>
          </button>
          <div id="mobile-resources-section" style={{
            maxHeight: mobileResourcesOpen ? 600 : 0,
            overflow: "hidden",
            transition: "max-height 0.3s ease",
          }}>
            <div style={{ padding: "8px 0 12px" }}>
              {RESOURCE_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={closeMenu}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", textDecoration: "none" }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: "rgba(79,205,196,0.08)", border: "1px solid rgba(79,205,196,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: TEAL,
                  }}>
                    {RESOURCE_ICONS[l.label]}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: TEXT, fontFamily: "var(--body)" }}>{l.label}</div>
                    <div style={{ fontSize: 12, fontWeight: 400, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>{l.desc}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <a href="/pricing" onClick={closeMenu} style={{ display: "block", padding: "12px 0", color: TEXT_MUTED, fontSize: 15, fontWeight: 500, borderBottom: "1px solid rgba(255,255,255,0.03)", textDecoration: "none" }}>Pricing</a>
          <a href="/integrations" onClick={closeMenu} style={{ display: "block", padding: "12px 0", color: TEXT_MUTED, fontSize: 15, fontWeight: 500, borderBottom: "1px solid rgba(255,255,255,0.03)", textDecoration: "none" }}>Integrations</a>
          <a href="/about" onClick={closeMenu} style={{ display: "block", padding: "12px 0", color: TEXT_MUTED, fontSize: 15, fontWeight: 500, borderBottom: "1px solid rgba(255,255,255,0.03)", textDecoration: "none" }}>About</a>
          </div>

          {/* Pinned footer (Profound pattern: vertically stacked buttons always visible, even when items above scroll). */}
          <div style={{
            flexShrink: 0,
            padding: "16px 20px 20px",
            borderTop: `1px solid ${CARD_BORDER}`,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}>
            <a href={utm("nav", "signin", "mobile_signin")} onClick={closeMenu}
              style={{ display: "block", padding: "12px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)", color: TEXT_MUTED, fontSize: 14, fontWeight: 600, textAlign: "center", textDecoration: "none" }}
            >Sign In</a>
            <a href={utm("nav", "signup", "mobile_get_started")} onClick={closeMenu}
              style={{ display: "block", padding: "12px 0", borderRadius: 10, background: TEAL, color: "#000", fontSize: 14, fontWeight: 600, textAlign: "center", textDecoration: "none" }}
            >Start Free Trial</a>
          </div>
        </div>
      )}
    </nav>
  );
}
